import { AfterViewInit, Component, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { SyncService } from '../../api/sync.service';
import { PaginationService } from '../../data/pagination/pagination.service';
import { Project } from '../../data/models/project';
import { ProjectFilteredList } from '../../data/models/project-filtered-list';
import { ProjectsApiService } from '../../api/projects-api.service';
import { ProjectType } from '../../data/models/project-type';
import { ProjectService } from '../../data/project/project.service';
import { UserService } from 'src/app/data/user/user.service';
import { ToasterService } from 'src/app/api/toaster.service';
import { Router } from '@angular/router';
import { CommonMessages } from 'src/app/constants/common-messages';
import { StringConstants } from 'src/app/constants/string-constants';
import { ProjectStatusEnum } from 'src/app/constants/project-status';
import { FabricJobStatus } from 'src/app/constants/fabric-workspace-constants';

@Component({
    selector: 'app-project-list',
    templateUrl: './project-list.component.html',
    styleUrls: ['./project-list.component.scss'],
    standalone: false
})
export class ProjectListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort = new MatSort();
  @Input() topPaginator: MatPaginator;
  @Input() columns: any[];
  @Input() projects: Project[];
  @Input() totalProjectsCount: number;
  @Input() tab: string;
  @Input() tabName: string;

  displayedColumns: any[] = [];
  dataSource: MatTableDataSource<Project>;
  columnsToDisplay: any;
  currentPageIndex = 1;
  currentOffset = 1;
  defaultPageSize: number;
  filterData: { [key: string]: any } = {};
  projectType: ProjectType[];
  defaultOffset: number;
  subscriptions: Subscription[] = [];
  fabricJobStatus: string;
  isFabricJobCompleted = false;
  doesFabricJobExist = false;
  projectStatusEnum = ProjectStatusEnum;

  constructor(
    private syncService: SyncService,
    private toasterService: ToasterService,
    private projectApiService: ProjectsApiService,
    private paginationService: PaginationService,
    private projectDataService: ProjectService,
    public userService: UserService,
    private router: Router) {
  }

  ngOnInit(): void {
    this.defaultPageSize = this.paginationService.defaultPageSize;
    this.displayedColumns = this.columns.map(col => col.value);
    this.displayedColumns.push('Action')
    this.dataSource = new MatTableDataSource(this.projects);

    this.paginationSubscriptions();

    // refreshing the datasource after filters applied
    this.subscriptions.push(this.syncService.refreshProjectListDatasource.asObservable().subscribe((project: any) => {
      this.projects = project[this.tab].slice(this.defaultOffset, this.defaultPageSize);
      this.dataSource = new MatTableDataSource(this.projects);
      this.totalProjectsCount = project[this.tab].length;
      this.currentPageIndex = 1;
      this.paginationService.updateRecordCount(project[this.tab].length);
    }));
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.topPaginator;
    this.sort.disableClear = true;
  }

  ngOnDestroy(): void {
    // prevent memory leak when component destroyed
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  openProjectSettingsPage(row: any): void {
    const isProjectAwaitingConfiguration = row.solutionOffering === '';
    const isAccessible = this.projectDataService.isProjectAccessible(row.status);
    if (isAccessible && !isProjectAwaitingConfiguration) {
      sessionStorage.setItem("projectName", row.projectTitle);
      this.router.navigateByUrl(`project/${row.projectId}/project-details`);
    }
    else if (isAccessible && isProjectAwaitingConfiguration) {
      this.toasterService.info(CommonMessages.PROJECT_CONFIGURATION_INCOMPLETE, StringConstants.ERROR);
    }
  }

  sortData(sort: Sort): void {
    let sortOrder: number;
    if (sort.direction == 'asc') {
      sortOrder = 0;
    }
    else {
      sortOrder = 1;
    }
    this.projectDataService.filterData['sortBy'] = sort.active;
    this.projectDataService.filterData['sortOrder'] = sortOrder;
    this.getProjectList();

  }

  paginationSubscriptions(): void {
    //refreshing the datasource to show n number of records as per page size
    this.subscriptions.push(this.syncService.pageSizeChangeNotifier.asObservable().subscribe(pageSize => {
      //assigning the default page size
      this.defaultPageSize = pageSize;
      this.paginationService.defaultPageSize = pageSize;
      this.getProjectList();
    }));

    //refreshing the data on page change
    this.subscriptions.push(this.syncService.offsetSizeChangeNotifier.asObservable().subscribe(offset => {
      this.currentOffset = offset;
      this.getProjectList();
    }));
  }

  getProjectList(): void {
    this.subscriptions.push(this.projectApiService.getProjectList(this.projectDataService.filterData, this.currentOffset, this.defaultPageSize, this.tabName).subscribe((projectList: ProjectFilteredList) => {
      //assigning the project type list
      this.projectType = projectList.projectType;
      this.projects = projectList[this.tab];
      this.paginationService.updateRecordCount(this.projects.length);
      //setting the project type value for all projects
      this.projectDataService.setProjectType(this.projects, this.projectType);

      this.dataSource = new MatTableDataSource(this.projects);
    }));
  }

  checkFabricJobStatus(row: Project): void {
    this.subscriptions.push(
      this.projectDataService.checkProjectFabricJobStatus(row.projectId).subscribe({
        next: (response) => {
          this.fabricJobStatus = response;

          if (this.fabricJobStatus === FabricJobStatus.Completed || this.fabricJobStatus === FabricJobStatus.Success) {
            this.toasterService.info(CommonMessages.WORKSPACE_CREATION_COMPLETED, StringConstants.SUCCESS);
            row.isFabricJobCompleted = true;
          }
          else if (this.fabricJobStatus === FabricJobStatus.InProgress || this.fabricJobStatus === FabricJobStatus.Running)
            this.toasterService.info(CommonMessages.WORKSPACE_CREATION_IN_PROGRESS, StringConstants.SUCCESS);
          else if (this.fabricJobStatus === FabricJobStatus.Failed)
            this.toasterService.info(CommonMessages.WORKSPACE_CREATION_FAILED, StringConstants.ERROR);

          this.syncService.refreshProjectList.next(true);
        }
      })
    );
  }
}

